import { Modal } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApimHandler } from '@api/apim-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { countEndpointsByGroup } from '@pages/api-portal/utils';
import { ApiGroup } from '@pages/api-portal/types';
import { GroupFormData } from './group-dialog';

/*
 * Encapsulates data fetching, search, and CRUD handlers for the API Groups manager.
 */
export function useGroupsManager() {
    const [groups, setGroups] = useState<ApiGroup[]>([]);
    const [endpointCounts, setEndpointCounts] = useState<Record<string, number>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<ApiGroup | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    /*
     * Loads groups and the full endpoint list (used only to compute per-group counts).
     */
    const fetchGroups = useCallback(async () => {
        setIsLoading(true);
        try {
            const [groupsRes, endpointsRes] = await Promise.all([
                ApimHandler.fetchApiGroups(),
                ApimHandler.fetchAllEndpoints(),
            ]);
            setGroups(groupsRes.data);
            setEndpointCounts(countEndpointsByGroup(endpointsRes.data));
        } catch {
            errorAlert('Failed to load API groups.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    const filteredGroups = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return groups;
        return groups.filter(
            (g) =>
                g.name?.toLowerCase().includes(q) ||
                g.description?.toLowerCase().includes(q) ||
                g.groupKey?.toLowerCase().includes(q),
        );
    }, [groups, searchQuery]);

    const openCreateDialog = useCallback(() => {
        setSelectedGroup(null);
        setIsFormDialogOpen(true);
    }, []);

    const openEditDialog = useCallback((group: ApiGroup) => {
        setSelectedGroup(group);
        setIsFormDialogOpen(true);
    }, []);

    /*
     * Creates or updates a group depending on whether selectedGroup is set. groupKey is
     * immutable and only sent on create.
     */
    const handleSubmit = useCallback(
        async (data: GroupFormData) => {
            setIsSubmitting(true);
            try {
                if (selectedGroup) {
                    await ApimHandler.updateApiGroup(String(selectedGroup.id), {
                        name: data.name,
                        description: data.description,
                        icon: data.icon,
                    });
                    successAlert('Group updated.');
                } else {
                    await ApimHandler.createApiGroup({
                        groupKey: data.groupKey,
                        name: data.name,
                        description: data.description,
                        icon: data.icon,
                    });
                    successAlert('Group created.');
                }
                setIsFormDialogOpen(false);
                setSelectedGroup(null);
                fetchGroups();
            } catch {
                errorAlert(selectedGroup ? 'Failed to update group.' : 'Failed to create group.');
            } finally {
                setIsSubmitting(false);
            }
        },
        [selectedGroup, fetchGroups],
    );

    /*
     * Confirms and deletes a group, warning if it still has endpoints assigned.
     */
    const handleDeleteRequest = useCallback(
        (group: ApiGroup) => {
            const count = endpointCounts[group.groupKey ?? String(group.id)] ?? 0;
            Modal.confirm({
                className: 'apimModal',
                title: 'Delete API Group',
                content:
                    count > 0
                        ? `This group contains ${count} endpoint(s). Are you sure you want to delete "${group.name}"? This action cannot be undone.`
                        : `Are you sure you want to delete "${group.name}"? This action cannot be undone.`,
                okText: 'Delete',
                cancelText: 'Cancel',
                okButtonProps: { style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' } },
                onOk: async () => {
                    try {
                        await ApimHandler.deleteApiGroup(String(group.id));
                        successAlert('Group deleted.');
                        fetchGroups();
                    } catch {
                        errorAlert('Failed to delete group.');
                    }
                },
            });
        },
        [endpointCounts, fetchGroups],
    );

    return {
        filteredGroups,
        endpointCounts,
        isLoading,
        searchQuery,
        setSearchQuery,
        isFormDialogOpen,
        setIsFormDialogOpen,
        selectedGroup,
        isSubmitting,
        openCreateDialog,
        openEditDialog,
        handleSubmit,
        handleDeleteRequest,
    };
}
